/**
 * Mirror of `keel_kernel::permissions`. Keep the two in sync; the backend is the authority and the
 * UI only uses this to hide what a user cannot do.
 *
 *  - `*` grants everything; `sales.*` a module; `sales.orders.*` a resource.
 *  - Module-level keys (the prototype's `sales.read` / `sales.write`) grant that action on every
 *    resource of the module. Requesting `sales.read` asks "can read anything in sales".
 *  - `manage` implies `write` and `read`; `write` implies `read`. Other actions are explicit.
 */
const ladder = (g: string, r: string) =>
  g === r || (g === "manage" && (r === "write" || r === "read")) || (g === "write" && r === "read");

export function grantAllows(granted: string, requested: string): boolean {
  if (granted === "*" || granted === requested) return true;
  if (granted.endsWith(".*")) {
    const prefix = granted.slice(0, -2);
    return requested.startsWith(`${prefix}.`);
  }
  const g = granted.split(".");
  const r = requested.split(".");
  // Module-level grant: `sales.write` ⇒ `sales.<any>.write|read`.
  if (g.length === 2) return r.length === 3 && g[0] === r[0] && ladder(g[1] ?? "", r[2] ?? "");
  // Module-level request: `sales.read` ⇐ any `sales.<res>.read|write|manage`.
  if (r.length === 2) return g.length === 3 && g[0] === r[0] && ladder(g[2] ?? "", r[1] ?? "");
  // Same resource, action ladder.
  return g.length === 3 && r.length === 3 && g[0] === r[0] && g[1] === r[1] && ladder(g[2] ?? "", r[2] ?? "");
}

export function can(granted: readonly string[], requested: string): boolean {
  return granted.some((g) => grantAllows(g, requested));
}
